// src/components/video/SecureHlsPlayer.jsx
import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { BASE } from "../../api/client";

function encPath(p) {
    return p.split("/").map(encodeURIComponent).join("/");
}


// 유저 토큰만 사용 (관리자 토큰 adm_token은 제외)
function getAccessToken() {
    try {
        const keys = ["access_token", "token", "Authorization"]; // 선호 순서
        for (const k of keys) {
            const v = localStorage.getItem(k);
            if (!v) continue;
            return v.startsWith("Bearer ") ? v.slice(7) : v;
        }
    } catch { }
    return null;
}

async function tryRefresh() {
    // 백엔드에 /auth/refresh가 있는 경우에만 동작
    try {
        const r = await fetch(`${BASE}/auth/refresh`, {
            method: "POST",
            credentials: "include",
        });
        if (!r.ok) return false;
        // 응답에 새 access_token을 주는 구조라면 저장
        const data = await r.json().catch(() => ({}));
        if (data?.access_token) {
            localStorage.setItem("access_token", data.access_token);
        }
        return true;
    } catch {
        return false;
    }
}

async function issuePlayToken(vidPath) {
    const url = `${BASE}/video/${encPath(vidPath)}/issue-play-token`;
    // 1차 시도
    let at = getAccessToken();
    let res = await fetch(url, {
        method: "POST",
        credentials: "include",                // 쿠키 인증 대비
        headers: at ? { Authorization: `Bearer ${at}` } : {},
    });

    // 401 → refresh 시도 후 재시도
    if (res.status === 401) {
        const ok = await tryRefresh();
        if (ok) {
            at = getAccessToken();
            res = await fetch(url, {
                method: "POST",
                credentials: "include",
                headers: at ? { Authorization: `Bearer ${at}` } : {},
            });
        }
    }
    if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`토큰 발급 실패 (${res.status}) ${t}`);
    }
    const { token } = await res.json();
    return token;
}



export default function SecureHlsPlayer({
    vidPath,
    poster = "",
    className = "",
    autoPlay = false,
}) {
    const videoRef = useRef(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);

    useEffect(() => {
        let hls;
        let aborted = false;

        async function run() {
            try {
                setErr("");
                setLoading(true);
                const encVid = encPath(vidPath);
                let token;
                try {
                    token = await issuePlayToken(vidPath);
                } catch (e) {
                    if (import.meta?.env?.REACT_APP_HLS_DEV === "1" || process.env.REACT_APP_HLS_DEV === "1") {
                        token = "DEV";
                    } else {
                        throw e;
                    }
                }

                if (token === "DEV") {

                    const video = videoRef.current;
                    if (!video) return;
                    const m3u8 = `${BASE}/video/${encVid}/playlist.m3u8?pt=DEV`;
                    if (Hls.isSupported()) {
                        hls = new Hls();
                        hls.loadSource(m3u8);
                        hls.attachMedia(video);
                    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
                        video.src = m3u8;
                    } else {
                        video.src = m3u8;
                    }
                    setLoading(false);
                    return;


                }

                const m3u8 = `${BASE}/video/${encVid}/playlist.m3u8?pt=${encodeURIComponent(token)}`;
                const video = videoRef.current;
                if (!video) return;

                if (autoPlay) {
                    video.muted = true;         // 대부분 브라우저 정책 상 필요
                    video.autoplay = true;
                    video.playsInline = true;   // iOS 사파리
                }

                // 2) HLS 로드
                if (Hls.isSupported()) {
                    hls = new Hls();
                    hls.loadSource(m3u8);
                    hls.attachMedia(video);

                    hls.on(Hls.Events.MANIFEST_PARSED, () => {
                        if (autoPlay) video.play().catch(() => { });
                    });

                } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
                    // Safari(iOS) 등은 natively 지원
                    video.src = m3u8;

                    if (autoPlay) {
                        const onCanPlay = () => {
                            video.play().catch(() => { });
                            video.removeEventListener("canplay", onCanPlay);
                        };
                        video.addEventListener("canplay", onCanPlay);
                    }
                } else {
                    video.src = m3u8;

                    if (autoPlay) {
                        const onCanPlay = () => {
                            video.play().catch(() => { });
                            video.removeEventListener("canplay", onCanPlay);
                        };
                        video.addEventListener("canplay", onCanPlay);
                    }
                }

                setLoading(false);
            } catch (e) {
                setErr(e?.message || "재생 오류");
                setLoading(false);
            }
        }

        run();
        return () => {
            aborted = true;
            if (hls) hls.destroy();
        };
    }, [vidPath]);

    // 재생/일시정지 상태 동기화
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        v.addEventListener("play", onPlay);
        v.addEventListener("pause", onPause);
        return () => {
            v.removeEventListener("play", onPlay);
            v.removeEventListener("pause", onPause);
        };
    }, []);

    // 클릭/키보드로 토글
    const togglePlay = () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) {
            v.play().catch(() => { });
        } else {
            v.pause();
        }
    };
    const onKeyToggle = (e) => {
        if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            togglePlay();
        }
    };


    if (err) {
        return (
            <div className="w-full aspect-video rounded-2xl border border-red-300 bg-red-50 flex items-center justify-center text-red-600">
                {err}
            </div>
        );
    }

    return (
        <div className={`relative ${className}`}>
            <video
                ref={videoRef}
                playsInline
                controls
                controlsList="nodownload noplaybackrate"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                poster={poster || undefined}
                className="w-full aspect-video rounded-2xl overflow-hidden bg-black cursor-pointer"
                onClick={togglePlay}
                onKeyDown={onKeyToggle}
                tabIndex={0}
                aria-label="영상 재생/일시정지"
                title="영상 재생/일시정지"
            />
            {/* {loading && <div className="mt-2 text-sm text-gray-500">영상을 준비하고 있어요…</div>} */}
        </div>
    );
}
